import React, { useEffect, useState, useContext, useRef } from "react";
import { getFinanceReport } from "../../../../Utility/Api";
import { getFormattedCurrentDate2, NaviGate, shop_id, getStoredEntriesPerPage } from "../../../../Utility/function";
import { WarehouseContext } from "../../../../Utility/UserContext";
import "./../table.css";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { Paginations } from "../../../Common/Functions";

// Helper function to get current week range
const getCurrentWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));

    return {
        start: firstDay.toISOString().split('T')[0],
        end: lastDay.toISOString().split('T')[0]
    };
};

// Helper function to get week range from date
const getWeekRange = (dateString) => {
    const date = new Date(dateString);
    const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));
    const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 6));

    return {
        start: firstDay.toISOString().split('T')[0],
        end: lastDay.toISOString().split('T')[0]
    };
};

const FinanceReport = () => {
    const navigate = useNavigate();
    const { showLoader, hideLoader, shopItem } = useContext(WarehouseContext);
    const contentRef = useRef(null);

    const [filterType, setFilterType] = useState(() => {
        return sessionStorage.getItem("finance_report_filterType") || "daily";
    });
    const [selectedDate, setSelectedDate] = useState(() => {
        return sessionStorage.getItem("finance_report_selectedDate") || new Date().toISOString().split('T')[0];
    });
    const [selectedWeek, setSelectedWeek] = useState(() => {
        try {
            const saved = sessionStorage.getItem("finance_report_selectedWeek");
            return saved ? JSON.parse(saved) : getCurrentWeek();
        } catch {
            return getCurrentWeek();
        }
    });
    const [selectedMonth, setSelectedMonth] = useState(() => {
        return sessionStorage.getItem("finance_report_selectedMonth") || getFormattedCurrentDate2().substring(0, 7);
    });
    const [selectedYear, setSelectedYear] = useState(() => {
        return sessionStorage.getItem("finance_report_selectedYear") || new Date().getFullYear().toString();
    });
    const [customFromDate, setCustomFromDate] = useState(() => {
        return sessionStorage.getItem("finance_report_customFromDate") || "";
    });
    const [customToDate, setCustomToDate] = useState(() => {
        return sessionStorage.getItem("finance_report_customToDate") || "";
    });

    const [inflowsData, setInflowsData] = useState({ customer_payments: { total: 0, items: [] }, supplier_returns: { total: 0, items: [] }, total: 0 });
    const [outflowsData, setOutflowsData] = useState({ supplier_payments: { total: 0, items: [] }, customer_refunds: { total: 0, items: [] }, processing_charges: { total: 0, items: [] }, general_expenses: { total: 0, items: [] }, total: 0 });
    const [netBalance, setNetBalance] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(getStoredEntriesPerPage(10));

    const [searchText, setSearchText] = useState("");
    const [companyInfo, setCompanyInfo] = useState({
        name: "Company Name",
        address: "123 Business Street, City, State 12345",
        phone: "(123) 456-7890",
        email: "info@yourcompany.com"
    });

    useEffect(() => {
        if (shopItem) {
            setCompanyInfo({
                name: shopItem?.title || "",
                address: shopItem?.Address || "123 Business Street, City, State 12345",
                phone: shopItem?.phone1 || "",
                email: shopItem?.email || ""
            });
        }
    }, [shopItem]);

    // Save filters to sessionStorage
    useEffect(() => {
        sessionStorage.setItem("finance_report_filterType", filterType);
    }, [filterType]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_selectedDate", selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_selectedWeek", JSON.stringify(selectedWeek));
    }, [selectedWeek]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_selectedMonth", selectedMonth);
    }, [selectedMonth]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_selectedYear", selectedYear);
    }, [selectedYear]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_customFromDate", customFromDate);
    }, [customFromDate]);

    useEffect(() => {
        sessionStorage.setItem("finance_report_customToDate", customToDate);
    }, [customToDate]);

    // Derive fromDate and toDate
    const getDateRange = () => {
        let startDate = "";
        let endDate = "";

        switch (filterType) {
            case "daily":
                startDate = selectedDate;
                endDate = selectedDate;
                break;
            case "weekly":
                startDate = selectedWeek.start;
                endDate = selectedWeek.end;
                break;
            case "monthly":
                startDate = `${selectedMonth}-01`;
                const lastDay = new Date(
                    new Date(startDate).getFullYear(),
                    new Date(startDate).getMonth() + 1,
                    0
                ).getDate();
                endDate = `${selectedMonth}-${lastDay}`;
                break;
            case "yearly":
                startDate = `${selectedYear}-01-01`;
                endDate = `${selectedYear}-12-31`;
                break;
            case "custom":
                startDate = customFromDate;
                endDate = customToDate;
                break;
            default:
                break;
        }

        return { fromDate: startDate, toDate: endDate };
    };

    const { fromDate, toDate } = getDateRange();

    const fetchData = async (from, to) => {
        try {
            showLoader();
            const filter = `from_date=${from}&to_date=${to}&shop_id=${shop_id || shopItem?.id}`;
            const response = await getFinanceReport(filter);
            if (response.data) {
                setInflowsData(response.data.inflows || { customer_payments: { total: 0, items: [] }, supplier_returns: { total: 0, items: [] }, total: 0 });
                setOutflowsData(response.data.outflows || { supplier_payments: { total: 0, items: [] }, customer_refunds: { total: 0, items: [] }, processing_charges: { total: 0, items: [] }, general_expenses: { total: 0, items: [] }, total: 0 });
                setNetBalance(response.data.net_balance || 0);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            console.error("Error fetching finance report data:", error);
        }
    };

    useEffect(() => {
        if (fromDate && toDate) {
            fetchData(fromDate, toDate);
        }
    }, [fromDate, toDate, shopItem]);

    const handleWeekChange = (dateString) => {
        setSelectedWeek(getWeekRange(dateString));
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const printContent = document.getElementById('printable-report').innerHTML;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Finance Report</title>
                <style>
                    @media print {
                        @page {
                            size: A4 portrait;
                            margin: 0.5in;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 11pt;
                            color: #000;
                            background: #fff;
                        }
                        .print-container {
                            width: 100%;
                            max-width: 21cm;
                            margin: 0 auto;
                        }
                        .print-header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #000;
                            padding-bottom: 15px;
                        }
                        .print-header h1 {
                            font-size: 20pt;
                            margin: 0;
                            color: #000;
                        }
                        .print-header p {
                            margin: 5px 0;
                            font-size: 10pt;
                        }
                        .report-info {
                            margin: 15px 0;
                            padding: 10px;
                            background-color: #f8f9fa;
                            border-radius: 5px;
                        }
                        .section-title {
                            font-size: 13pt;
                            font-weight: bold;
                            margin-top: 20px;
                            margin-bottom: 10px;
                            border-bottom: 1px solid #ccc;
                            padding-bottom: 5px;
                        }
                        .report-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 10px 0 20px 0;
                            font-size: 9pt;
                        }
                        .report-table th, .report-table td {
                            border: 1px solid #ddd;
                            padding: 6px;
                            text-align: left;
                        }
                        .report-table th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        .report-totals {
                            margin-top: 20px;
                            padding: 15px;
                            border: 1px solid #ddd;
                            border-radius: 5px;
                            background-color: #f9f9f9;
                        }
                        .total-row {
                            display: flex;
                            justify-content: space-between;
                            margin: 5px 0;
                        }
                        .total-amount {
                            font-weight: bold;
                            font-size: 12pt;
                        }
                        .text-success {
                            color: #28a745 !important;
                        }
                        .text-danger {
                            color: #dc3545 !important;
                        }
                        .footer {
                            margin-top: 30px;
                            text-align: center;
                            font-size: 9pt;
                            color: #6c757d;
                        }
                    }
                </style>
            </head>
            <body>
                ${printContent}
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() {
                            window.close();
                        }, 100);
                    };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
    };

    const filterItems = (items) => {
        if (!searchText) return items;
        const search = searchText.toLowerCase();
        return items.filter(item => 
            String(item.order_id).toLowerCase().includes(search) ||
            String(item.type).toLowerCase().includes(search)
        );
    };

    const exportToExcel = () => {
        const inflowsList = [
            ...inflowsData.customer_payments.items,
            ...inflowsData.supplier_returns.items
        ];
        
        const outflowsList = [
            ...outflowsData.supplier_payments.items,
            ...outflowsData.customer_refunds.items,
            ...outflowsData.processing_charges.items,
            ...outflowsData.general_expenses.items
        ];

        const exportInflows = filterItems(inflowsList).map((item) => ({
            Date: item.date,
            "Reference / Order ID": item.order_id,
            Type: item.type,
            "Payment Mode": item.mop,
            "Amount (INR)": item.amount
        }));

        const exportOutflows = filterItems(outflowsList).map((item) => ({
            Date: item.date,
            "Reference / Order ID": item.order_id,
            Type: item.type,
            "Payment Mode": item.mop,
            "Amount (INR)": item.amount
        }));

        const wb = XLSX.utils.book_new();
        
        const wsIn = XLSX.utils.json_to_sheet(exportInflows);
        XLSX.utils.book_append_sheet(wb, wsIn, "Inflows");

        const wsOut = XLSX.utils.json_to_sheet(exportOutflows);
        XLSX.utils.book_append_sheet(wb, wsOut, "Outflows");

        XLSX.writeFile(wb, `Finance_Report_${fromDate}_to_${toDate}.xlsx`);
    };

    const renderFilterControls = () => {
        switch (filterType) {
            case "daily":
                return (
                    <div className="col-auto">
                        <div className="form-floating">
                            <input
                                type="date"
                                className="form-control"
                                id="selectedDate"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            />
                            <label htmlFor="selectedDate">Select Date</label>
                        </div>
                    </div>
                );
            case "weekly":
                return (
                    <div className="col-auto">
                        <div className="form-floating">
                            <input
                                type="date"
                                className="form-control"
                                id="weekDate"
                                value={selectedWeek.start}
                                onChange={(e) => handleWeekChange(e.target.value)}
                            />
                            <label htmlFor="weekDate">Select Week</label>
                        </div>
                    </div>
                );
            case "monthly":
                return (
                    <div className="col-auto">
                        <div className="form-floating">
                            <input
                                type="month"
                                className="form-control"
                                id="selectedMonth"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            />
                            <label htmlFor="selectedMonth">Select Month</label>
                        </div>
                    </div>
                );
            case "yearly":
                return (
                    <div className="col-auto">
                        <div className="form-floating">
                            <input
                                type="number"
                                className="form-control"
                                id="selectedYear"
                                min="2000"
                                max="2100"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            />
                            <label htmlFor="selectedYear">Select Year</label>
                        </div>
                    </div>
                );
            case "custom":
                return (
                    <>
                        <div className="col-auto">
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    id="customFromDate"
                                    value={customFromDate}
                                    onChange={(e) => setCustomFromDate(e.target.value)}
                                />
                                <label htmlFor="customFromDate">From Date</label>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    id="customToDate"
                                    value={customToDate}
                                    onChange={(e) => setCustomToDate(e.target.value)}
                                />
                                <label htmlFor="customToDate">To Date</label>
                            </div>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    // Construct flat lists for screen rendering with standard search filters applied
    const flatInflows = filterItems([
        ...inflowsData.customer_payments.items,
        ...inflowsData.supplier_returns.items
    ]);

    const flatOutflows = filterItems([
        ...outflowsData.supplier_payments.items,
        ...outflowsData.customer_refunds.items,
        ...outflowsData.processing_charges.items,
        ...outflowsData.general_expenses.items
    ]);

    const displayedInflowsTotal = flatInflows.reduce((sum, item) => sum + item.amount, 0);
    const displayedOutflowsTotal = flatOutflows.reduce((sum, item) => sum + item.amount, 0);
    const displayedNet = displayedInflowsTotal - displayedOutflowsTotal;

    // Combine into a single chronologically sorted list of transactions
    const combinedTransactions = [
        ...flatInflows.map(item => ({ ...item, inflow: item.amount, outflow: 0 })),
        ...flatOutflows.map(item => ({ ...item, inflow: 0, outflow: item.amount }))
    ];
    combinedTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    const totalPages = Math.ceil(combinedTransactions.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = combinedTransactions.slice(startIndex, startIndex + rowsPerPage);

    // Calculate page and grand totals
    const pageInflowTotal = paginatedData.reduce((sum, item) => sum + (item.inflow || 0), 0);
    const pageOutflowTotal = paginatedData.reduce((sum, item) => sum + (item.outflow || 0), 0);
    const grandInflowTotal = combinedTransactions.reduce((sum, item) => sum + (item.inflow || 0), 0);
    const grandOutflowTotal = combinedTransactions.reduce((sum, item) => sum + (item.outflow || 0), 0);

    return (
        <div className="page-content py-1">
            <NaviGate />
            <div className="card p-3 mx-2 overflow-y-scroll" style={{ minHeight: "90vh" }} ref={contentRef}>
                {/* Header controls block */}
                <div className="row mb-4 border-bottom justify-content-between align-items-center w-100 pb-3">
                    <h5 className="text-start w-auto fw-bold mb-0">
                        Finance Report
                    </h5>
                    <div className="w-auto d-flex gap-2 mt-0 justify-content-end align-items-center">
                        <div className="col-auto ps-0">
                            <div className="form-floating">
                                <select
                                    className="form-select"
                                    id="filterType"
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                >
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                                <label htmlFor="filterType">Filter By</label>
                            </div>
                        </div>

                        {renderFilterControls()}

                        <div className="col-md-3 me-2" style={{ display: "none" }}>
                            <div className="form-floating">
                                <input
                                    className="form-control"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search by ID or Type"
                                />
                                <label>Search Reference / Type</label>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-dark me-2 py-2"
                            onClick={handlePrint}
                        >
                            Print Report
                        </button>
                        
                        <button
                            type="button"
                            className="btn btn-success py-2"
                            onClick={exportToExcel}
                            style={{ display: "none" }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>

                <div className="d-flex justify-content-between mb-3 text-muted">
                    <div>
                        <strong>Date Range:</strong> {fromDate} to {toDate}
                    </div>
                </div>

                {/* Net Summary Cards (Hidden as requested, but code preserved) */}
                <div className="row g-3 mb-4" style={{ display: "none" }}>
                    <div className="col-md-4">
                        <div className="card shadow-sm border-0 bg-light p-3 text-start">
                            <span className="text-muted small fw-bold">TOTAL INFLOWS</span>
                            <h3 className="text-success fw-bold mb-0 mt-1">₹{displayedInflowsTotal.toFixed(2)}</h3>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card shadow-sm border-0 bg-light p-3 text-start">
                            <span className="text-muted small fw-bold">TOTAL OUTFLOWS</span>
                            <h3 className="text-danger fw-bold mb-0 mt-1">₹{displayedOutflowsTotal.toFixed(2)}</h3>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card shadow-sm border-0 bg-light p-3 text-start">
                            <span className="text-muted small fw-bold">NET BALANCE</span>
                            <h3 className={`fw-bold mb-0 mt-1 ${displayedNet >= 0 ? 'text-success' : 'text-danger'}`}>
                                ₹{displayedNet.toFixed(2)}
                            </h3>
                        </div>
                    </div>
                </div>

                {/* Single Combined Report Table */}
                <div className="table-responsive text-center position-relative my-2 w-100">
                    <table className="table table-sm table-bordered table-hover">
                        <thead className="table-dark">
                            <tr>
                                <th>S.No</th>
                                <th>Date</th>
                                <th>Reference / Order ID</th>
                                <th>Transaction Type</th>
                                <th>Payment Mode</th>
                                <th>Inflow (Credit)</th>
                                <th>Outflow (Debit)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((item, index) => (
                                    <tr key={index}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>{item.date}</td>
                                        <td style={{ whiteSpace: "normal", wordBreak: "break-all" }}>{item.order_id}</td>
                                        <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{item.type}</td>
                                        <td>{item.mop}</td>
                                        <td className="text-success fw-semibold">
                                            {item.inflow > 0 ? `₹${item.inflow.toFixed(2)}` : "-"}
                                        </td>
                                        <td className="text-danger fw-semibold">
                                            {item.outflow > 0 ? `₹${item.outflow.toFixed(2)}` : "-"}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">No transactions found</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="fw-bold bg-light">
                                <td colSpan="5" className="text-end">Page Totals:</td>
                                <td className="text-success">₹{pageInflowTotal.toFixed(2)}</td>
                                <td className="text-danger">₹{pageOutflowTotal.toFixed(2)}</td>
                            </tr>
                            <tr className="fw-bold bg-light">
                                <td colSpan="5" className="text-end">Grand Totals:</td>
                                <td className="text-success">₹{grandInflowTotal.toFixed(2)}</td>
                                <td className="text-danger">₹{grandOutflowTotal.toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <Paginations
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        entriesPerPage={rowsPerPage}
                        onEntriesPerPageChange={(val) => {
                            setRowsPerPage(val);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                {/* Printable Report (Hidden from screen view) */}
                <div id="printable-report" style={{ display: 'none' }}>
                    <div className="print-container">
                        <div className="print-header">
                            <h1>{companyInfo.name}</h1>
                            <p>{companyInfo.address}</p>
                            <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                            <h2>Finance Report</h2>
                        </div>

                        <div className="report-info">
                            <p><strong>Report Period:</strong> {fromDate} to {toDate}</p>
                            <p><strong>Generated On:</strong> {new Date().toLocaleDateString()}</p>
                            {searchText && (
                                <p><strong>Filtered by:</strong> {searchText}</p>
                            )}
                        </div>

                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Date</th>
                                    <th>Reference / Order ID</th>
                                    <th>Transaction Type</th>
                                    <th>Payment Mode</th>
                                    <th>Inflow (Credit)</th>
                                    <th>Outflow (Debit)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinedTransactions.length > 0 ? (
                                    combinedTransactions.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{item.date}</td>
                                            <td>{item.order_id}</td>
                                            <td>{item.type}</td>
                                            <td>{item.mop}</td>
                                            <td style={{ color: '#28a745', fontWeight: 'bold' }}>
                                                {item.inflow > 0 ? `₹${item.inflow.toFixed(2)}` : "-"}
                                            </td>
                                            <td style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                                {item.outflow > 0 ? `₹${item.outflow.toFixed(2)}` : "-"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center' }}>No transactions found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className="report-totals">
                            <h3>Summary</h3>
                            <div className="total-row">
                                <span>Total Inflows (Credit):</span>
                                <span className="text-success total-amount">₹{displayedInflowsTotal.toFixed(2)}</span>
                            </div>
                            <div className="total-row">
                                <span>Total Outflows (Debit):</span>
                                <span className="text-danger total-amount">₹{displayedOutflowsTotal.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="footer">
                            <p>Report generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinanceReport;
